// ────────────────────────────────────────────────────────────────────
// DockBar — the fixed bottom-right strip that hosts every registered
// dock window. Desktop: all open (non-minimized) windows render side by
// side, newest nearest the corner (windows are exposed oldest→newest by
// DockContext, so a plain right-aligned row puts the newest rightmost).
// Narrow (<640px, matching Tailwind's `sm` breakpoint): only the FRONT
// window renders at full width; the rest become a compact switcher row
// of chips — never two windows side by side on mobile.
//
// This is the only `position: fixed` element in the dock system — windows
// and pills are laid out in-flow inside it.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { useDock } from "./DockContext";
import { DockWindow } from "./DockWindow";
import { DockPill } from "./DockPill";

const NARROW_QUERY = "(max-width: 639px)";

/** Where the dock's bottom edge sits, and how tall its button row is.
 *
 *  Exported because the dock is `fixed` at z-1200 in the bottom-right corner
 *  and it is NOT the only thing that wants that corner — IntakeScreen's upload
 *  panel is fixed there too, at z-1150, i.e. underneath. While the panel was
 *  only ever opened from a file manager nobody used, the overlap never showed.
 *  Now that uploading is the primary action of /intake, an upload in progress
 *  would have drawn its rows directly beneath these buttons: measured on
 *  production 2026-08-04, 227 of the panel's 360px sat under the dock.
 *
 *  Anything else placed in this corner must clear DOCK_TOP_PX rather than pick
 *  its own number and hope. `usesBottomRightCorner` in DockBar.test.tsx fails
 *  if a new offset lands inside the dock's band.
 */
export const DOCK_BOTTOM_PX = 36;
export const DOCK_HEIGHT_PX = 36;
export const DOCK_TOP_PX = DOCK_BOTTOM_PX + DOCK_HEIGHT_PX;

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState<boolean>(() => (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(NARROW_QUERY).matches
      : false
  ));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(NARROW_QUERY);
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mql.matches);
    // Safari <14 has no addEventListener on MediaQueryList — addListener is
    // the legacy fallback both jsdom-in-tests and old Safari understand.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return narrow;
}

export function DockBar(): JSX.Element | null {
  const { windows, minimize, restore, closeWindow, openWindow } = useDock();
  const narrow = useIsNarrow();

  const openWindows = windows.filter((w) => w.open && !w.minimized);
  // Anything not shown as a full window — minimized OR fully closed — lives in
  // the launcher tray as a pill so it is ALWAYS reopenable. Without this a
  // window closed via its × would be unreachable (only the minimize pill could
  // restore it), stranding a persistent dock like MODS/chat.
  const dockedWindows = windows.filter((w) => !(w.open && !w.minimized));

  // A registered window can be launched from the tray even when nothing is open.
  if (windows.length === 0) return null;

  // Reopen from the tray: mark it open AND bring it to front / un-minimize.
  const launch = (id: string): void => { openWindow(id); restore(id); };

  // `openWindows` is ordered oldest→newest — the last entry is the most
  // recently opened/restored/switched-to, i.e. the front window.
  const front = openWindows[openWindows.length - 1];
  const visibleWindows = narrow ? (front ? [front] : []) : openWindows;
  const switcherWindows = narrow && front ? openWindows.filter((w) => w.id !== front.id) : [];

  return (
    <div data-testid="dock-bar" style={{ bottom: DOCK_BOTTOM_PX }}
         className="fixed right-4 z-[1200] flex flex-col items-end gap-2 pointer-events-none">
      {visibleWindows.length > 0 && (
        <div className="flex items-end justify-end gap-2 pointer-events-auto">
          {visibleWindows.map((w) => (
            <DockWindow
              key={w.id}
              id={w.id}
              title={w.title}
              icon={w.icon}
              body={w.body}
              badge={w.badge}
              onMinimize={() => minimize(w.id)}
              onClose={() => closeWindow(w.id)}
            />
          ))}
        </div>
      )}

      {switcherWindows.length > 0 && (
        // Bring-to-front reuses `restore` (open + un-minimize + newest
        // openedAt) — an already-open, non-minimized window switching to
        // front is the same state transition as un-minimizing one.
        <div
          className="flex items-center flex-wrap justify-end gap-1.5 pointer-events-auto"
          role="tablist"
          aria-label="สลับหน้าต่าง"
        >
          {switcherWindows.map((w) => (
            <button
              key={w.id}
              type="button"
              role="tab"
              onClick={() => restore(w.id)}
              aria-label={`สลับไปที่ ${w.title}`}
              title={w.title}
              className="flex items-center gap-1 px-2 h-7 bg-panel-850 border border-panel-700 hover:border-brand text-ink-3 hover:text-brand font-mono text-[9px] uppercase tracking-wider"
            >
              {w.icon}
              <span className="max-w-[80px] truncate">{w.title}</span>
            </button>
          ))}
        </div>
      )}

      {dockedWindows.length > 0 && (
        <div className="flex items-center flex-wrap justify-end gap-1.5 pointer-events-auto">
          {dockedWindows.map((w) => (
            <DockPill key={w.id} id={w.id} title={w.title} icon={w.icon} unread={w.unread} onRestore={launch} />
          ))}
        </div>
      )}
    </div>
  );
}
